/**
 * Hōjin — Bandeau de consentement cookies (CNIL).
 * 3 actions au même niveau visuel : Tout accepter / Tout refuser / Personnaliser.
 * Catégories non-essentielles OFF par défaut.
 */
import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  acceptAll,
  getConsent,
  rejectAll,
  setConsent,
  type ConsentState,
} from "@/lib/cookie-consent";

const GOLD = "#FF1744";
const BLACK = "#0A0A0A";
const INK = "#F5F1E8";
const DIM = "#9A9089";

export function CookieBanner() {
  const [open, setOpen] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [monitoring, setMonitoring] = useState(false);

  useEffect(() => {
    const initial = getConsent();
    if (!initial.decided) {
      setOpen(true);
    }
    setMonitoring(initial.categories.monitoring);

    const openPrefs = () => {
      const c = getConsent();
      setMonitoring(c.categories.monitoring);
      setShowPrefs(true);
      setOpen(true);
    };
    window.addEventListener("nanae:open-cookie-preferences", openPrefs);
    return () => window.removeEventListener("nanae:open-cookie-preferences", openPrefs);
  }, []);

  if (!open) return null;

  const finish = (_state: ConsentState) => {
    setOpen(false);
    setShowPrefs(false);
  };

  const handleAccept = () => finish(acceptAll());
  const handleReject = () => finish(rejectAll());
  const handleSave = () => finish(setConsent({ monitoring }));

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-banner-title"
      style={{
        position: "fixed",
        bottom: 16,
        left: 16,
        right: 16,
        zIndex: 9999,
        maxWidth: 720,
        margin: "0 auto",
        background: BLACK,
        color: INK,
        border: `1px solid ${GOLD}`,
        padding: "20px 22px",
        fontFamily: '"Playfair Display", Georgia, serif',
        boxShadow: "0 18px 48px rgba(0,0,0,0.55)",
      }}
    >
      <p
        id="cookie-banner-title"
        style={{
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: 10,
          letterSpacing: "2px",
          textTransform: "uppercase",
          color: GOLD,
          margin: "0 0 10px",
        }}
      >
        Cookies · Vie privée
      </p>
      <p style={{ margin: "0 0 16px", fontSize: 14, lineHeight: 1.55 }}>
        Hōjin utilise des cookies strictement nécessaires au fonctionnement du
        service (session d'authentification). Avec votre accord, nous activons
        également des outils de mesure et de monitoring pour améliorer la
        plateforme. Vous pouvez accepter, refuser ou personnaliser vos choix —
        refuser est aussi simple qu'accepter.{" "}
        <Link
          to="/politique-cookies"
          style={{ color: GOLD, textDecoration: "underline" }}
          onClick={() => setOpen(false)}
        >
          En savoir plus
        </Link>
        .
      </p>

      {showPrefs && (
        <div
          style={{
            border: `1px solid ${GOLD}33`,
            padding: "14px 16px",
            marginBottom: 16,
            background: "#000",
          }}
        >
          <CategoryRow
            title="Essentiels"
            description="Session d'authentification. Indispensables au service, déposés sans consentement."
            enabled
            locked
          />
          <CategoryRow
            title="Mesure & monitoring"
            description="Détection d'erreurs (Sentry) pour stabiliser le service. Désactivés par défaut."
            enabled={monitoring}
            onChange={setMonitoring}
          />
        </div>
      )}

      <div
        style={{
          display: "flex",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        {showPrefs ? (
          <BannerButton primary onClick={handleSave}>
            Enregistrer mes choix
          </BannerButton>
        ) : (
          <>
            <BannerButton primary onClick={handleAccept}>
              Tout accepter
            </BannerButton>
            <BannerButton primary onClick={handleReject}>
              Tout refuser
            </BannerButton>
            <BannerButton primary onClick={() => setShowPrefs(true)}>
              Personnaliser
            </BannerButton>
          </>
        )}
      </div>
    </div>
  );
}

function BannerButton({
  children,
  onClick,
  primary,
}: {
  children: React.ReactNode;
  onClick: () => void;
  primary?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: "1 1 auto",
        minWidth: 140,
        minHeight: 44,
        background: "transparent",
        color: GOLD,
        border: `1px solid ${GOLD}`,
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: 11,
        letterSpacing: "2px",
        textTransform: "uppercase",
        padding: "10px 16px",
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

function CategoryRow({
  title,
  description,
  enabled,
  locked,
  onChange,
}: {
  title: string;
  description: string;
  enabled: boolean;
  locked?: boolean;
  onChange?: (v: boolean) => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 16,
        padding: "10px 0",
        borderBottom: `1px solid ${GOLD}1F`,
      }}
    >
      <div style={{ flex: 1 }}>
        <p
          style={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: 11,
            letterSpacing: "1.5px",
            textTransform: "uppercase",
            color: INK,
            margin: "0 0 4px",
          }}
        >
          {title}
        </p>
        <p style={{ fontSize: 13, color: DIM, margin: 0, lineHeight: 1.5 }}>
          {description}
        </p>
      </div>
      <label
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          cursor: locked ? "not-allowed" : "pointer",
          opacity: locked ? 0.6 : 1,
        }}
      >
        <span
          style={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: 10,
            letterSpacing: "1.5px",
            color: enabled ? GOLD : DIM,
          }}
        >
          {enabled ? "ON" : "OFF"}
        </span>
        <input
          type="checkbox"
          checked={enabled}
          disabled={locked}
          onChange={(e) => onChange?.(e.target.checked)}
          aria-label={`Activer ${title}`}
          style={{ accentColor: GOLD, width: 18, height: 18 }}
        />
      </label>
    </div>
  );
}
